import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert, TextField,
  InputAdornment, FormControl, InputLabel, Select, MenuItem, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, TablePagination,
  Stack, Paper, Tooltip, IconButton, Tabs, Tab, FormControlLabel, Switch
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon, Close as CloseIcon,
  ReportProblemOutlined as UnresolvedIcon, CheckCircle as ResolveIcon
} from '@mui/icons-material';
import bomService from '../../services/bomService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

const BOM_REASONS = [
  'assembly_unresolved',
  'no_resolvable_components',
  'all_components_unresolved',
];
const JOB_REASONS = [
  'no_resolvable_assembly',
  'undefined_type_transmutation',
  'empty_draft',
];

function reasonChip(reason) {
  return <Chip size="small" label={reason || '—'} variant="outlined" color="warning" />;
}

// CIN7 SKUs carry a geo prefix like `EU_SS0001282049`. The geo is already
// shown in its own column, so strip the prefix from displayed SKUs to keep
// the table readable. Raw value is preserved on hover.
const KNOWN_GEOS = ['EU', 'UK', 'US', 'AU', 'SG', 'CA', 'MY', 'AMZ', 'ALL'];
function stripGeoPrefix(sku, geo) {
  if (!sku) return sku;
  const s = String(sku);
  if (geo && s.startsWith(`${geo}_`)) return s.slice(geo.length + 1);
  for (const g of KNOWN_GEOS) {
    if (s.startsWith(`${g}_`)) return s.slice(g.length + 1);
  }
  return s;
}

function fmtDate(v) {
  if (!v) return '—';
  const s = String(v);
  if (s.startsWith('1970-01-01') || s === '0000-00-00 00:00:00') return '—';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}

function fmtDateTime(v) {
  if (!v) return '—';
  const s = String(v);
  if (s.startsWith('1970-01-01') || s === '0000-00-00 00:00:00') return '—';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

function renderSkuList(skus, geo) {
  const arr = Array.isArray(skus) ? skus : [];
  if (arr.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {arr.map((s, i) => {
        const stripped = stripGeoPrefix(s, geo);
        const showTip = stripped !== s;
        const chip = <Chip key={i} size="small" label={stripped} sx={{ fontFamily: 'monospace', mb: 0.25 }} variant="outlined" />;
        return showTip ? <Tooltip key={i} title={`raw: ${s}`}>{chip}</Tooltip> : chip;
      })}
    </Stack>
  );
}

export default function AdminUnresolved() {
  const [tab, setTab] = useState('boms');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // filters
  const [includeResolved, setIncludeResolved] = useState(false);
  const [reasonFilter, setReasonFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // resolve dialog
  const [resolveDialog, setResolveDialog] = useState({ open: false, row: null, note: '' });
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: page + 1, page_size: pageSize };
      if (includeResolved) params.include_resolved = '1';
      if (reasonFilter) params.reason = reasonFilter;
      if (tab === 'boms' && search) params.search = search;
      const data = tab === 'boms'
        ? await bomService.listUnresolvedBoms(params)
        : await bomService.listUnresolvedJobs(params);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to load'));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tab, page, pageSize, includeResolved, reasonFilter, search]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // reset page + filters when switching tabs (job tab has no search)
  const handleTabChange = (_, v) => {
    setTab(v);
    setPage(0);
    setReasonFilter('');
    setSearchInput('');
    setSearch('');
  };

  const openResolve = (row) => {
    setResolveDialog({ open: true, row, note: '' });
    setResolveError(null);
  };

  const handleResolve = async () => {
    const { row, note } = resolveDialog;
    if (!row) return;
    setResolving(true);
    setResolveError(null);
    try {
      if (tab === 'boms') {
        await bomService.resolveUnresolvedBom(row.cin7_bom_id, note);
      } else {
        await bomService.resolveUnresolvedJob(row.cin7_job_id, note);
      }
      setResolveDialog({ open: false, row: null, note: '' });
      // Re-fetch to reflect new state. RMT mutations are sync (mutations_sync=1
      // server-side) so the next list query will see the update.
      fetchRows();
    } catch (e) {
      setResolveError(apiErrorMessage(e, 'Failed to resolve'));
    } finally {
      setResolving(false);
    }
  };

  const reasons = tab === 'boms' ? BOM_REASONS : JOB_REASONS;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <UnresolvedIcon color="warning" />
        <Typography variant="h5">CIN7 Unresolved</Typography>
        <Typography variant="body2" color="text.secondary">— Import bucket awaiting mapping</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh">
          <IconButton onClick={fetchRows}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value="boms" label="BOMs" />
          <Tab value="jobs" label="Production Jobs" />
        </Tabs>
      </Paper>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            {tab === 'boms' && (
              <TextField
                size="small"
                label="Search SKU or BOM ref"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ minWidth: 240 }}
              />
            )}
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Unresolved reason</InputLabel>
              <Select value={reasonFilter} label="Unresolved reason" onChange={(e) => { setReasonFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All reasons</MenuItem>
                {reasons.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch size="small" checked={includeResolved} onChange={(e) => { setIncludeResolved(e.target.checked); setPage(0); }} />}
              label="Include resolved"
            />
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              {tab === 'boms' ? (
                <TableRow>
                  <TableCell>BOM ref</TableCell>
                  <TableCell>Assembly Code</TableCell>
                  <TableCell>Geo</TableCell>
                  <TableCell align="center">Comps</TableCell>
                  <TableCell align="center">Unresolved</TableCell>
                  <TableCell>Unresolved SKUs</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Ingested</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell>WO Ref</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell align="center">Make</TableCell>
                  <TableCell align="center">Use</TableCell>
                  <TableCell align="center">Undefined</TableCell>
                  <TableCell>Unresolved SKUs</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={tab === 'boms' ? 10 : 11} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={tab === 'boms' ? 10 : 11} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No rows.</Typography></TableCell></TableRow>
              ) : tab === 'boms' ? rows.map((r) => (
                <TableRow key={r.cin7_bom_id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.cin7_bom_reference}</Typography></TableCell>
                  <TableCell>
                    {(() => {
                      const stripped = stripGeoPrefix(r.cin7_assembly_code, r.geography);
                      const node = <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{stripped}</Typography>;
                      return stripped !== r.cin7_assembly_code ? <Tooltip title={`raw: ${r.cin7_assembly_code}`}>{node}</Tooltip> : node;
                    })()}
                  </TableCell>
                  <TableCell>{r.geography ? <Chip size="small" label={r.geography} variant="outlined" /> : '—'}</TableCell>
                  <TableCell align="center">{r.num_components}</TableCell>
                  <TableCell align="center">{r.num_unresolved}</TableCell>
                  <TableCell>{renderSkuList(r.unresolved_skus, r.geography)}</TableCell>
                  <TableCell>{reasonChip(r.unresolved_reason)}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{fmtDateTime(r.ingested_at)}</Typography></TableCell>
                  <TableCell align="center">
                    {r.is_resolved
                      ? <Chip size="small" color="success" label="Resolved" />
                      : <Chip size="small" color="warning" label="Open" variant="outlined" />}
                  </TableCell>
                  <TableCell align="center">
                    {r.is_resolved ? null : (
                      <Tooltip title="Mark resolved">
                        <IconButton size="small" color="success" onClick={() => openResolve(r)}>
                          <ResolveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              )) : rows.map((r) => (
                <TableRow key={r.cin7_job_id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.cin7_reference}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>{r.project_name || '—'}</Typography></TableCell>
                  <TableCell>{fmtDate(r.due_date)}</TableCell>
                  <TableCell>{fmtDate(r.completed_date)}</TableCell>
                  <TableCell align="center">{r.num_make_rows}</TableCell>
                  <TableCell align="center">{r.num_use_rows}</TableCell>
                  <TableCell align="center">{r.num_undefined_rows}</TableCell>
                  <TableCell>{renderSkuList(r.unresolved_skus)}</TableCell>
                  <TableCell>{reasonChip(r.unresolved_reason)}</TableCell>
                  <TableCell align="center">
                    {r.is_resolved
                      ? <Chip size="small" color="success" label="Resolved" />
                      : <Chip size="small" color="warning" label="Open" variant="outlined" />}
                  </TableCell>
                  <TableCell align="center">
                    {r.is_resolved ? null : (
                      <Tooltip title="Mark resolved">
                        <IconButton size="small" color="success" onClick={() => openResolve(r)}>
                          <ResolveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>

      {/* Resolve confirm dialog */}
      <Dialog open={resolveDialog.open} onClose={() => !resolving && setResolveDialog({ open: false, row: null, note: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Mark {tab === 'boms' ? 'BOM' : 'Job'} as resolved
          <IconButton
            onClick={() => setResolveDialog({ open: false, row: null, note: '' })}
            disabled={resolving}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          ><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {resolveDialog.row && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This marks the row as resolved (is_resolved=1) but does NOT re-ingest.
                Add the missing SKU to product_master, then re-run the CIN7 ingest with <code>--replace-cin7</code> to materialize.
              </Alert>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {tab === 'boms' ? (
                  <>
                    <Typography variant="body2"><strong>BOM ref:</strong> <code>{resolveDialog.row.cin7_bom_reference}</code></Typography>
                    <Typography variant="body2"><strong>Assembly:</strong> <code>{resolveDialog.row.cin7_assembly_code}</code></Typography>
                    <Typography variant="body2"><strong>Reason:</strong> {resolveDialog.row.unresolved_reason}</Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2"><strong>WO ref:</strong> <code>{resolveDialog.row.cin7_reference}</code></Typography>
                    <Typography variant="body2"><strong>Project:</strong> {resolveDialog.row.project_name || '—'}</Typography>
                    <Typography variant="body2"><strong>Reason:</strong> {resolveDialog.row.unresolved_reason}</Typography>
                  </>
                )}
              </Stack>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label="Note (optional)"
                placeholder="What did you do to resolve this?"
                value={resolveDialog.note}
                onChange={(e) => setResolveDialog({ ...resolveDialog, note: e.target.value })}
                disabled={resolving}
              />
              {resolveError && <Alert severity="error" sx={{ mt: 2 }}>{resolveError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog({ open: false, row: null, note: '' })} disabled={resolving}>Cancel</Button>
          <Button onClick={handleResolve} variant="contained" color="success" disabled={resolving}>
            {resolving ? <CircularProgress size={20} /> : 'Mark resolved'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
